import { Box, Icon, SxProps } from "@outschool/backpack";
import { IconDefinition, faCopy } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import {
  TrackedButton,
  TrackedButtonProps,
} from "@outschool/ui-components-shared";
import { Input } from "@outschool/ui-legacy-component-library";
// @ts-ignore
import copy from "copy-to-clipboard";
import React, { useCallback, useEffect, useRef } from "react";

import { getExtraMetaData, uiContextIds } from "./Referrals";

interface CopyToClipboardProps {
  isVisible?: boolean;
  inputValue: string;
  trackingName?: string;
  trackingUniqueId?: string;
  trackingMetaData?: Record<string, string>;
  copyButtonLabel?: string;
  buttonColor?: TrackedButtonProps["color"];
  buttonVariant?: TrackedButtonProps["variant"];
  uiContextId?: string;
  inputId?: string;
  hideInput?: boolean;
  label?: string;
  sx?: SxProps;
  buttonSx?: TrackedButtonProps["sx"];
  inputSx?: React.ComponentProps<typeof Input>["sx"];
  inputStyles?: any;
}

export const CopyToClipboard = ({
  inputValue,
  copyButtonLabel,
  buttonColor,
  buttonVariant,
  trackingName,
  trackingUniqueId,
  trackingMetaData,
  uiContextId,
  hideInput,
  inputId,
  label,
  isVisible = false,
  sx = {},
  inputSx = {},
  inputStyles = {}, // inputStyles is used for CSS precedence
  buttonSx = {},
}: CopyToClipboardProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const focusInput = useCallback(() => {
    if (inputValue) {
      const input = inputRef.current;
      input?.setSelectionRange(0, inputValue.length);
      input?.focus();
    }
  }, [inputValue]);

  const blurInput = useCallback(() => {
    inputRef.current?.blur();
  }, []);

  // Focus the input whenever isVisible becomes true.
  useEffect(() => {
    if (isVisible) {
      focusInput();
    }
  }, [isVisible, focusInput]);

  // Blur the input when the component unmounts.
  useEffect(() => () => blurInput(), [blurInput]);

  return (
    <Box
      flex
      sx={[
        {
          flexDirection: "column",
          position: "relative",
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {label && !hideInput && (
        <Box
          sx={{
            color: "grey.700",
            fontWeight: 500,
            marginLeft: "24px",
            textAlign: "left",
          }}
        >
          {label}
        </Box>
      )}
      <Box
        flex
        sx={{
          position: "relative",
          justifyContent: "flex-start",
          alignItems: "stretch",
          gap: "0.5em",
        }}
      >
        {hideInput ? null : (
          <Input
            id={inputId}
            style={{
              borderRadius: "24px 0 0 24px",
              height: "auto",
              marginRight: -32,
              paddingRight: "32px",
              ...inputStyles,
            }}
            sx={{
              width: "100%",
              ...inputSx,
            }}
            type="text"
            readOnly
            value={inputValue ?? ""}
            ref={inputRef}
            onClick={focusInput}
          />
        )}
        <CopyToClipboardButton
          value={inputValue}
          onCopy={focusInput}
          trackingName={trackingName}
          trackingUniqueId={trackingUniqueId}
          trackingMetaData={trackingMetaData}
          label={copyButtonLabel}
          color={buttonColor}
          variant={buttonVariant}
          uiContextId={uiContextId}
          // @ts-ignore TS(2322): Type 'IconDefinition | null' is not assignable to ... Remove this comment to see the full error message
          icon={hideInput ? faCopy : null}
          sx={[
            { ...(hideInput ? { flex: 1 } : {}) },
            ...(Array.isArray(buttonSx) ? buttonSx : [buttonSx]),
          ]}
        />
      </Box>
    </Box>
  );
};

interface CopyToClipboardButtonProps {
  value: string;
  trackingName?: string;
  trackingUniqueId?: string;
  onCopy?: (text: string) => void;
  label?: string;
  icon?: IconDefinition;
  color?: TrackedButtonProps["color"];
  variant?: TrackedButtonProps["variant"];
  sx?: TrackedButtonProps["sx"];
  uiContextId?: string;
  trackingMetaData?: Record<string, string>;
  spaceLeft?: boolean;
}

export function CopyToClipboardButton({
  value,
  trackingName,
  trackingUniqueId,
  onCopy = () => {},
  icon,
  variant,
  color,
  sx = {},
  label: labelProp,
  uiContextId = uiContextIds.NotAssigned,
  trackingMetaData = {},
}: CopyToClipboardButtonProps) {
  const { t } = useTranslation(
    "ui-components-website\\Sharing\\CopyToClipboard"
  );
  const label = labelProp ?? t`Copy Link`;
  const [justCopied, setJustCopied] = React.useState(false);
  const timeoutRef = React.useRef<NodeJS.Timeout>();

  React.useEffect(() => {
    return () => {
      // @ts-ignore TS(2769): No overload matches this call.
      clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleClipboard = () => {
    copy(value);
    setJustCopied(true);
    // @ts-ignore TS(2769): No overload matches this call.
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setJustCopied(false);
    }, 3000);
    onCopy(value);
  };

  return (
    <TrackedButton
      color={color}
      variant={variant}
      onClick={handleClipboard}
      startIcon={
        icon ? (
          <Icon
            icon={icon}
            sx={{
              fontSize: "1.333em",
            }}
          />
        ) : undefined
      }
      trackingName={trackingName}
      trackingUniqueId={trackingUniqueId}
      trackingSharedProperties={{
        uiContextId,
        ...trackingMetaData,
        ...getExtraMetaData(value),
      }}
      sx={[{ minWidth: "min-content" }, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      {justCopied ? t`Copied!` : label}
    </TrackedButton>
  );
}
